/**
 * Engraving Checkbox UI
 * Injects the complimentary engraving checkbox after variant selects on PDPs
 * and inside the quick-view drawer (on any page where quick view is enabled).
 * Respects window.theme.settings.engravable on PDPs;
 * uses /products/<handle>.js tags check inside the quick view.
 */
(function () {
  'use strict';

  var _uid = 0;

  // ── BUILD & WIRE ─────────────────────────────────────────────────────────────

  function buildEngravingSection(variantSelects, formId, uid) {
    var section = document.createElement('div');
    section.className = 'product-form__input engraving-section';
    // Keep legacy id on first instance (PDP) so existing CSS / selectors still work
    if (uid === 1) section.id = 'engraving-section';

    section.innerHTML = [
      '<label class="engraving-section__toggle" for="engraving-toggle-' + uid + '" style="display:flex;align-items:center;gap:10px;cursor:pointer;">',
        '<input type="checkbox" id="engraving-toggle-' + uid + '" class="engraving-toggle" style="width:16px;height:16px;min-width:16px;min-height:16px;flex-shrink:0;cursor:pointer;margin:0;padding:0;appearance:auto;-webkit-appearance:checkbox;">',
        '<span>Add complimentary engraving (Optional)</span>',
        '<a href="/pages/engraving" target="_blank" class="engraving-learn-more" style="display:inline-flex;align-items:center;gap:4px;font-size:1em;text-decoration:none;margin-left:auto;">',
          '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>',
          'Learn More',
        '</a>',
      '</label>',
      '<div class="engraving is-hidden" style="margin-top:16px;">',
        '<div class="product-form__input product-form__input--block">',
          '<div class="form__label">Font: <span class="form__label__value engraving-font-label">Bold</span></div>',
          '<input type="radio" id="engraving-bold-' + uid + '" name="properties[Font]" value="Bold" form="' + formId + '">',
          '<label for="engraving-bold-' + uid + '" style="margin-right:16px;"><span>Bold</span></label>',
          '<input type="radio" id="engraving-slim-' + uid + '" name="properties[Font]" value="Slim" form="' + formId + '">',
          '<label for="engraving-slim-' + uid + '"><span>Slim</span></label>',
        '</div>',
        '<div class="product-form__input product-form__input--block" style="margin-top:16px;">',
          '<label for="engraving-text-' + uid + '" class="form__label">Engraving text: <span class="form__label__value">(Up to 6 characters)</span></label>',
          '<input id="engraving-text-' + uid + '" type="text" name="properties[Engraving text]" form="' + formId + '" maxlength="6" class="product-information--line-item--text">',
        '</div>',
      '</div>'
    ].join('');

    variantSelects.insertAdjacentElement('afterend', section);

    var checkbox  = section.querySelector('.engraving-toggle');
    var details   = section.querySelector('.engraving');
    var boldRadio = section.querySelector('[id^="engraving-bold"]');
    var slimRadio = section.querySelector('[id^="engraving-slim"]');
    var textInput = section.querySelector('[name="properties[Engraving text]"]');
    var fontLabel = section.querySelector('.engraving-font-label');

    function updateFontLabel() {
      if (!fontLabel) return;
      if (boldRadio && boldRadio.checked) fontLabel.textContent = 'Bold';
      else if (slimRadio && slimRadio.checked) fontLabel.textContent = 'Slim';
    }

    if (boldRadio) boldRadio.addEventListener('change', updateFontLabel);
    if (slimRadio) slimRadio.addEventListener('change', updateFontLabel);

    if (checkbox) checkbox.addEventListener('change', function () {
      if (this.checked) {
        details.classList.remove('is-hidden');
        if (boldRadio) { boldRadio.checked = true; boldRadio.required = true; }
        if (textInput) textInput.required = true;
        updateFontLabel();
      } else {
        details.classList.add('is-hidden');
        if (boldRadio) { boldRadio.required = false; boldRadio.checked = false; }
        if (slimRadio) { slimRadio.required = false; slimRadio.checked = false; }
        if (textInput) { textInput.required = false; textInput.value = ''; }
      }
    });
  }

  // ── INIT (container-aware) ────────────────────────────────────────────────────

  function initEngravingCheckbox(container, skipEngravableCheck) {
    container = container || document;

    if (!skipEngravableCheck) {
      if (!window.theme || !window.theme.settings || !window.theme.settings.engravable) return;
    }

    var variantSelects = container.querySelector('variant-selects');
    if (!variantSelects) return;
    if (container.querySelector('.engraving-section')) return;

    var uid = ++_uid;
    var sectionId = variantSelects.getAttribute('data-section') || '';
    var formId = 'product-form-' + sectionId;
    buildEngravingSection(variantSelects, formId, uid);
  }

  // ── CART: STRIP "NO ENGRAVING" FROM VARIANT TITLE TEXT ───────────────────────

  var NO_ENGRAVE_RE = /[\s,\/]*no\s+engraving[\s,\/]*/gi;

  function stripNoEngravingFromNode(node) {
    if (node.nodeType !== Node.TEXT_NODE) return;
    if (!node.textContent || !/no\s+engraving/i.test(node.textContent)) return;
    var parent = node.parentElement;
    if (parent && (parent.tagName === 'INPUT' || parent.tagName === 'TEXTAREA' || parent.tagName === 'SELECT')) return;
    node.textContent = node.textContent.replace(NO_ENGRAVE_RE, '').trim();
  }

  function walkAndStrip(root) {
    var walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, null, false);
    var node;
    while ((node = walker.nextNode())) {
      stripNoEngravingFromNode(node);
    }
  }

  function hideNoEngravingFromCart() {
    var containers = document.querySelectorAll('#cart-drawer, .cart-drawer, [id*="cart"], [class*="cart-item"], [class*="line-item"]');
    if (containers.length) {
      containers.forEach(function (c) { walkAndStrip(c); });
    } else {
      walkAndStrip(document.body);
    }
  }

  var cartObserver = new MutationObserver(function (mutations) {
    mutations.forEach(function (m) {
      m.addedNodes.forEach(function (node) {
        if (node.nodeType === Node.ELEMENT_NODE) walkAndStrip(node);
      });
    });
  });

  function startCartObserver() {
    cartObserver.observe(document.body, { childList: true, subtree: true });
  }

  // ── QUICK VIEW DRAWER ─────────────────────────────────────────────────────────
  // On collection pages, window.theme.settings.engravable is always false
  // (it's set from Liquid's product variable which is null on non-product pages).
  // Instead, watch for content loading into #Product-Drawer and check engravability
  // via the Shopify product JSON API using the product handle from data-url.

  (function watchQuickViewDrawerForEngraving() {
    var drawer = document.getElementById('Product-Drawer');
    if (!drawer) return;

    var initTimer = null;

    new MutationObserver(function (mutations) {
      var hasChange = mutations.some(function (m) {
        return m.type === 'childList' && m.addedNodes.length > 0;
      });
      if (!hasChange) return;

      clearTimeout(initTimer);
      initTimer = setTimeout(function () {
        var vs = drawer.querySelector('variant-selects');
        if (!vs || drawer.querySelector('.engraving-section')) return;

        // Derive product handle from data-url, e.g. "/products/london-dominion-tennis-bracelet"
        var productUrl = vs.getAttribute('data-url') || '';
        var match = productUrl.match(/\/products\/([^/?#]+)/);
        var handle = match ? match[1] : '';

        if (!handle) {
          // Can't check — show engraving by default
          initEngravingCheckbox(drawer, true);
          return;
        }

        // Use Shopify AJAX product JSON to check for noengraving tag
        fetch('/products/' + handle + '.js')
          .then(function (r) { return r.json(); })
          .then(function (product) {
            var tags = Array.isArray(product.tags)
              ? product.tags
              : String(product.tags || '').split(', ');
            var noEngrave = tags.some(function (t) {
              return /noengraving/i.test(String(t).trim());
            });
            if (!noEngrave) initEngravingCheckbox(drawer, true);
          })
          .catch(function () {
            // On error, default to showing engraving
            initEngravingCheckbox(drawer, true);
          });
      }, 200);
    }).observe(drawer, { childList: true, subtree: true });
  })();

  // ── PAGE INIT ───────────────────────────────────────────────────────────────────

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initEngravingCheckbox();
      hideNoEngravingFromCart();
      startCartObserver();
    });
  } else {
    initEngravingCheckbox();
    hideNoEngravingFromCart();
    startCartObserver();
  }

  document.addEventListener('cart:updated', hideNoEngravingFromCart);
  document.addEventListener('cart:refresh', hideNoEngravingFromCart);
})();
